import type { ClosingPackageProjection } from '../types';
import { buildSendScansDocumentRows } from './sendScansScanRows';

export type ScanArchiveDocumentEntry = {
  documentId: number;
  zipEntryName: string;
  documentLabel: string;
};

const ZIP_ENTRY_FORBIDDEN_CHARS = new Set(['<', '>', ':', '"', '/', '\\', '|', '?', '*']);

function sanitizeZipEntryName(value: string): string {
  const sanitized = String(value || '')
    .trim()
    .split('')
    .map((ch) => {
      const code = ch.charCodeAt(0);
      if (code < 32 || ZIP_ENTRY_FORBIDDEN_CHARS.has(ch)) {
        return '_';
      }
      return ch;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .slice(0, 180);
  return sanitized;
}

function ensureUniqueName(name: string, used: Set<string>): string {
  let candidate = name;
  let index = 2;
  while (used.has(candidate.toLowerCase())) {
    const dot = name.lastIndexOf('.');
    if (dot > 0) {
      candidate = `${name.slice(0, dot)} (${index})${name.slice(dot)}`;
    } else {
      candidate = `${name} (${index})`;
    }
    index += 1;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

/** Documents included in the scan-dispatch ZIP (ready to send only). */
export function collectScanArchiveDocumentEntries(
  projection: ClosingPackageProjection
): ScanArchiveDocumentEntry[] {
  const usedNames = new Set<string>();
  const entries: ScanArchiveDocumentEntry[] = [];

  for (const row of buildSendScansDocumentRows(projection)) {
    if (!row.readyToSend) continue;

    const documentId =
      row.item.compositionMeta?.linkedDocumentId ?? row.item.documents[0]?.id ?? null;
    if (documentId == null) continue;

    const baseName = sanitizeZipEntryName(row.fileName || row.documentLabel || `document-${documentId}`);
    const zipEntryName = ensureUniqueName(baseName || `document-${documentId}`, usedNames);

    entries.push({
      documentId: Number(documentId),
      zipEntryName,
      documentLabel: row.documentLabel,
    });
  }

  return entries;
}

export function buildScanArchiveDownloadFilename(applicationId: number): string {
  return `closing-package-scans-${applicationId}.zip`;
}
